import { Suspense, useCallback, useState } from 'react';
import { FiltersButton } from 'common/components/Filters/FiltersButton';
import {
  chosenIdTypeFilterAtom,
  iccidRangeFilterAtom,
  searchTextFilterAtom,
  simFiltersAtom,
} from 'atoms/filters/sim';
import { useAtom, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { syncHintsAtom, updateHintsAtom } from 'atoms/hints/sim';
import { SimFiltersDialog } from './SimFiltersDialog';
import { SimFiltersDialogAtomInitializator } from './Components/Atoms/SimFiltersDialogAtomInitializator';
import Box from '@mui/material/Box';
import {
  TabBar,
  TabBarActions,
  TabBarChips,
  TabBarFilters,
} from 'common/components/AppLayout/TabBar/TabBar';
import { simsInventorySearchDataTestId } from 'simInventory/SimInventoryDataTestIds';
import Grid from '@mui/material/Grid';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton';
import { Actions, Subjects } from 'permissions/ability';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { SimProfileDownloadReportsExtendedFiltersButtonLargeExport } from 'simInventory/Reports/SimProfileDownloadReportsExtendedFiltersButtonLargeExport';
import { SimInventoryFilterChips } from './Components/Chips/SimInventoryFilterChips';
import { useHasActiveFilters } from './hooks/useHasActiveFilters';
import { useIsFetching } from '@tanstack/react-query';
import { removeNullValuesFromHintsObject } from 'src/common/utils/removeNullValuesFromHintsObject';
import { useSimDebouncedIdSearch } from 'src/common/components/Sim/Filters/useSimDebouncedIdSearch';
import { SimIdSearchInput } from 'src/common/components/Sim/Filters/SimIdSearchInput';
import { simInventoryQueries } from '../Inventory/queries/simInventoryQueries';
import { normalizeCustomFields } from 'src/common/components/Filters/Inputs/customFields/untils/normalizeCustomFields';

export const SimFilters = () => {
  const hasChips = useHasActiveFilters();
  const isFetching = useIsFetching({ queryKey: simInventoryQueries.all });

  const [open, setOpen] = useState(false);

  const setFilters = useSetAtom(simFiltersAtom);
  const resetFilters = useResetAtom(simFiltersAtom);

  const setHints = useSetAtom(updateHintsAtom);
  const syncHints = useSetAtom(syncHintsAtom);

  const onApply = useCallback((filters: any) => {
    setFilters(filters);
    const normalizedFilters = {
      ...filters,
      customFields: normalizeCustomFields(filters.customFields),
    };

    setHints(removeNullValuesFromHintsObject(normalizedFilters));
    setOpen(false);
  }, []);

  const onClose = useCallback(() => {
    syncHints();
    setOpen(false);
  }, []);

  const [chosenIdType, setChosenIdType] = useAtom(chosenIdTypeFilterAtom);
  const { inputValue, onChange, stopDebouncing } = useSimDebouncedIdSearch({
    iccidRangeFilterAtom,
    searchTextFilterAtom,
  });

  return (
    <TabBar>
      <Grid container rowGap={4} sx={{ mt: 0, width: '100%' }} justifyContent={'space-between'}>
        <TabBarFilters>
          <Box sx={{ flex: 1, maxWidth: '22.5rem' }}>
            <SimIdSearchInput
              chosenId={chosenIdType}
              onChosenIdChange={setChosenIdType}
              search={inputValue}
              onSearchChange={onChange}
            />
          </Box>
          <Suspense>
            <div data-testid={simsInventorySearchDataTestId}>
              <FiltersButton
                onClick={() => {
                  stopDebouncing();
                  setOpen(true);
                }}
                active={false}
              />
              {open && (
                <SimFiltersDialogAtomInitializator>
                  <SimFiltersDialog
                    onApply={onApply}
                    onCancel={onClose}
                    onClear={resetFilters}
                    onClose={onClose}
                  />
                </SimFiltersDialogAtomInitializator>
              )}
            </div>
          </Suspense>
        </TabBarFilters>
        <TabBarActions>
          <RefetchQueryButton queryKey={simInventoryQueries.all} />
          <SimInventoryCan I={Actions.export} a={Subjects.simsProfiles}>
            <SimProfileDownloadReportsExtendedFiltersButtonLargeExport disabled={!!isFetching} />
          </SimInventoryCan>
        </TabBarActions>
      </Grid>
      <TabBarChips>{hasChips ? <SimInventoryFilterChips /> : null}</TabBarChips>
    </TabBar>
  );
};
